import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mitteilungen } from '../api/apiClient';
import MitteilungAntwortAktionen, { MitteilungAntwortBadge } from './MitteilungAntwortAktionen';
import { WETTKAMPF_STATUS_BADGE_CLASS, WETTKAMPF_STATUS_LABELS } from '../constants';
import { formatDatumZeit } from '../utils/format';
import { artInfo, istVergangen, zeitangabe } from '../utils/kalender';

/**
 * Ein Kalendereintrag als Karte – dieselbe Darstellung in der Listenansicht und
 * im Tagesfenster der Monatsansicht.
 *
 * Was die Karte anbietet, hängt an der Art: ein Termin lässt sich hier
 * beantworten und als Kalenderdatei laden, ein Wettkampf führt zu seiner
 * Mannschaft und seinem Ergebnis, ein Saison-Eckdatum zur Saison.
 *
 * `zeigeEtiketten` blendet die Empfänger-Etiketten aus (z. B. im kompakten
 * Dashboard-Ausschnitt, wo nicht interessiert, für welche Funktionen ein
 * Termin bestimmt ist).
 */
function KalenderEintragKarte({ eintrag, onAntwort, antwortend = false, onFehler, zeigeEtiketten = true }) {
  const [icalLaeuft, setIcalLaeuft] = useState(false);

  const info = artInfo(eintrag.art);
  const vergangen = istVergangen(eintrag);
  const termin = eintrag.termin ?? null;
  const wettkampf = eintrag.wettkampf ?? null;

  const handleIcal = async () => {
    setIcalLaeuft(true);
    try {
      await Mitteilungen.downloadIcal(termin.mitteilungsId, eintrag.titel);
    } catch (e) {
      onFehler?.(`Kalenderdatei konnte nicht geladen werden: ${e.message}`);
    } finally {
      setIcalLaeuft(false);
    }
  };

  const statusLabel = wettkampf ? WETTKAMPF_STATUS_LABELS[wettkampf.status] ?? wettkampf.status : null;
  const gestrichen = wettkampf?.status === 'gestrichen';
  const mapsUrl = eintrag.ort ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(eintrag.ort)}` : null;

  return (
    <article className={`card border-l-4 p-4 ${info.rand} ${gestrichen ? 'opacity-60' : vergangen ? 'opacity-70' : ''}`}>
      <div className="mb-1.5 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className={`badge ${info.badge}`}>{info.einzahl}</span>
          <h3 className={`text-sm font-semibold text-slate-900 ${gestrichen ? 'line-through text-slate-400' : ''}`}>{eintrag.titel}</h3>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {statusLabel && (
            <span className={`badge ${WETTKAMPF_STATUS_BADGE_CLASS[wettkampf.status] ?? 'badge-gray'}`}>
              {statusLabel}
            </span>
          )}
          {termin && (
            <span title={termin.beantwortetAm ? `Beantwortet am ${formatDatumZeit(termin.beantwortetAm)}` : undefined}>
              <MitteilungAntwortBadge
                mitteilung={{ antwort: termin.antwort, zusageErforderlich: termin.zusageErforderlich }}
              />
            </span>
          )}
        </div>
      </div>

      <p className="flex items-start gap-1.5 text-sm text-slate-700">
        <CalendarIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
        <span>
          {zeitangabe(eintrag)}
          {eintrag.untertitel ? ` · ${eintrag.untertitel}` : ''}
        </span>
      </p>

      {eintrag.ort && (
        <p className="mt-0.5 flex items-start gap-1.5 text-sm text-slate-600">
          <LocationIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <span className="min-w-0">{eintrag.ort}</span>
        </p>
      )}

      {eintrag.beschreibung && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{eintrag.beschreibung}</p>
      )}

      {((zeigeEtiketten && eintrag.etiketten?.length > 0) || termin || wettkampf || eintrag.saisonId != null || mapsUrl) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-2.5">
          {zeigeEtiketten && (
            <div className="flex flex-wrap gap-1.5">
              {(eintrag.etiketten ?? []).map((etikett) => (
                <span key={etikett} className="badge badge-gray">{etikett}</span>
              ))}
            </div>
          )}

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {mapsUrl && (
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-sm gap-1.5 no-underline"
                title="Route in Google Maps öffnen"
              >
                <LocationIcon className="h-3.5 w-3.5" />
                Route
              </a>
            )}
            {wettkampf?.eigeneMannschaftsId != null && (
              <Link to={`/mannschaft/${wettkampf.eigeneMannschaftsId}`} className="btn btn-secondary btn-sm no-underline">
                Mannschaft
              </Link>
            )}
            {wettkampf != null && eintrag.saisonId != null && wettkampf.nummer != null && (
              <Link
                to={`/saison/${eintrag.saisonId}/wettkampf/${wettkampf.nummer}/ergebnis`}
                className="btn btn-secondary btn-sm no-underline"
              >
                Wettkampf
              </Link>
            )}
            {eintrag.wettkampf == null && eintrag.saisonId != null && (
              <Link to={`/saison/${eintrag.saisonId}`} className="btn btn-secondary btn-sm no-underline">
                Saison
              </Link>
            )}
            {termin && (
              <button
                type="button"
                onClick={handleIcal}
                disabled={icalLaeuft}
                className="btn btn-secondary btn-sm gap-1.5"
                title="Termin in den Kalender übernehmen (.ics)"
              >
                <DownloadIcon className="h-3.5 w-3.5" />
                {icalLaeuft ? 'Wird geladen…' : 'Kalender'}
              </button>
            )}
            {/* Ein vergangener Termin braucht keine Rückmeldung mehr. */}
            {termin && !vergangen && onAntwort && (
              <MitteilungAntwortAktionen
                mitteilung={{ antwort: termin.antwort, zusageErforderlich: termin.zusageErforderlich }}
                onAntwort={(antwort) => onAntwort(eintrag, antwort)}
                laufend={antwortend}
                klein
              />
            )}
          </div>
        </div>
      )}
    </article>
  );
}

function CalendarIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function LocationIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M12 2C8.686 2 6 4.686 6 8c0 5.25 6 13 6 13s6-7.75 6-13c0-3.314-2.686-6-6-6z" />
      <circle cx="12" cy="8" r="2" />
    </svg>
  );
}

function DownloadIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export default KalenderEintragKarte;
